import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { BrainEdgeRelation, BrainReviewDecision } from '@this+that/models';

import type {
  BrainAssertionView,
  BrainBackfillPlanView,
  BrainBlueprintInput,
  BrainBlueprintView,
  BrainDiscoveryRunView,
  BrainDiscoverySources,
  BrainEdgeRef,
  BrainEdgeView,
  BrainEntityView,
  BrainSignalTemplateTextsInput,
  BrainSlackChannelOption,
  BrainStats,
  ConfirmBrainEntityInput,
  EstimateBrainBackfillInput,
} from '../models/companyBrain';

const NETWORK_ONLY = 'network-only';

const BRAIN_BLUEPRINT_ITEM_PROVENANCE_FIELDS = gql`
  fragment BrainBlueprintItemProvenanceFields on BrainBlueprintItemProvenance {
    origin
    confidence
    signals
  }
`;

const BRAIN_BLUEPRINT_FIELDS = gql`
  fragment BrainBlueprintFields on BrainBlueprint {
    teamId
    version
    status
    sources {
      slackChannelIds
      emailEnabled
    }
    categories
    areas {
      key
      name
      spaceId
      provenance {
        ...BrainBlueprintItemProvenanceFields
      }
      memberUserIds
    }
    entityAliases {
      entityId
      type
      name
      aliases
      areaKeys
      parentEntityId
      provenance {
        ...BrainBlueprintItemProvenanceFields
      }
    }
    reviewerUserIds
    reviewTaskGroupId
    processing {
      debounceMinutes
      dailyExtractionCap
      reviewMode
      autoAcceptConfidence
    }
    createdBy
    createdAt
    activatedBy
    activatedAt
    changeSummary
    discoveryRunId
    suggestedSignals {
      templateId
      status
      reason {
        entityCount
        factCount
        entityTypes
      }
    }
    suggestedPeople {
      personRef
      displayName
      title
      emailDomain
      areaKeys
      evidence
      confidence
      summary
      reportsToRef
      reportsToConfidence
      reportsToEvidence
      confirmed
      stats {
        sent
        received
        threads
        firstSeen
        lastSeen
      }
    }
  }
  ${BRAIN_BLUEPRINT_ITEM_PROVENANCE_FIELDS}
`;

const BRAIN_ASSERTION_VIEW_FIELDS = gql`
  fragment BrainAssertionViewFields on BrainAssertionView {
    teamId
    assertionId
    revision
    subjectEntityId
    subjectName
    category
    predicate
    objectKind
    objectValue
    statement
    modality
    effectiveAt
    areaKeys
    spaceId
    classification
    reviewState
    knowledgeState
    publicationState
    evidence {
      evidenceId
      sourceType
      channel
      messageId
      excerpt
      collectedAt
      readable
    }
    supersedesAssertionId
    supersededByAssertionId
    mirrorOfAssertionId
    mirroredByAssertionId
    provenance {
      extractorModelId
      promptVersion
      pipelineVersion
      blueprintVersion
      reviewCardId
      decidedBy
    }
    createdAt
    updatedAt
  }
`;

const GET_BRAIN_BLUEPRINT = gql`
  query brainBlueprint($teamId: ID!) {
    brainBlueprint(teamId: $teamId) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const GET_BRAIN_BLUEPRINT_VERSIONS = gql`
  query brainBlueprintVersions($teamId: ID!) {
    brainBlueprintVersions(teamId: $teamId) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const GET_BRAIN_SLACK_CHANNEL_OPTIONS = gql`
  query brainSlackChannelOptions($teamId: ID!) {
    brainSlackChannelOptions(teamId: $teamId) {
      channelId
      name
      isPrivate
      workspaceId
      workspaceName
    }
  }
`;

const GET_BRAIN_STATS = gql`
  query brainStats($teamId: ID!) {
    brainStats(teamId: $teamId) {
      teamId
      activeAssertions
      pendingReviews
      extractionsToday
      dailyCap
      skippedToday {
        day
        byReason {
          reason
          count
        }
      }
      entitiesNeedingConfirmation
    }
  }
`;

const BRAIN_ENTITY_VIEW_FIELDS = gql`
  fragment BrainEntityViewFields on BrainEntityView {
    teamId
    entityId
    type
    canonicalName
    aliases
    areaKeys
    activeAssertionCount
    origin
    status
    parentEntityId
    mergedIntoEntityId
    projectionPages {
      spaceId
      pageId
    }
    isTeammate
    isFormerMember
    membership
    title
    summary
    coworkerEvidence
    stats {
      sent
      received
      threads
      firstSeen
      lastSeen
    }
    reportsTo {
      entityId
      confidence
      status
      manager {
        entityId
        canonicalName
      }
    }
  }
`;

const BRAIN_BACKFILL_PLAN_FIELDS = gql`
  fragment BrainBackfillPlanFields on BrainBackfillPlanView {
    planId
    sourceType
    channel
    fromDate
    toDate
    estimatedThreads
    estimatedExtractions
    estimateTruncated
    processedThreads
    skippedThreads
    extractedThreads
    cap
    status
    pausedReason
    createdAt
    updatedAt
  }
`;

// Three levels (roots, children, grandchildren); deeper levels are reached through the entity page.
const GET_BRAIN_ENTITY_TREE = gql`
  query brainEntityTree($teamId: ID!, $areaKey: String) {
    brainEntityTree(teamId: $teamId, areaKey: $areaKey) {
      ...BrainEntityViewFields
      children {
        ...BrainEntityViewFields
        children {
          ...BrainEntityViewFields
        }
      }
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const GET_BRAIN_ENTITIES_NEEDING_CONFIRMATION = gql`
  query brainEntitiesNeedingConfirmation($teamId: ID!, $limit: Int) {
    brainEntitiesNeedingConfirmation(teamId: $teamId, limit: $limit) {
      ...BrainEntityViewFields
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const GET_BRAIN_GRAPH = gql`
  query brainGraph(
    $teamId: ID!
    $entityId: ID!
    $relation: BrainEdgeRelation
  ) {
    brainGraph(teamId: $teamId, entityId: $entityId, relation: $relation) {
      relation
      direction
      origin
      status
      firstSeen
      lastSeen
      count
      channelId
      visibleSourceAssertionIds
      suggested
      neighbour {
        ...BrainEntityViewFields
      }
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const GET_BRAIN_PEOPLE = gql`
  query brainPeople($teamId: ID!) {
    brainPeople(teamId: $teamId) {
      ...BrainEntityViewFields
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const GET_BRAIN_BACKFILL_PLANS = gql`
  query brainBackfillPlans($teamId: ID!) {
    brainBackfillPlans(teamId: $teamId) {
      ...BrainBackfillPlanFields
    }
  }
  ${BRAIN_BACKFILL_PLAN_FIELDS}
`;

const CONFIRM_BRAIN_ENTITY = gql`
  mutation confirmBrainEntity(
    $teamId: ID!
    $entityId: ID!
    $name: String
    $type: BrainEntityType
    $parentEntityId: ID
  ) {
    confirmBrainEntity(
      teamId: $teamId
      entityId: $entityId
      name: $name
      type: $type
      parentEntityId: $parentEntityId
    ) {
      ...BrainEntityViewFields
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const MERGE_BRAIN_ENTITY = gql`
  mutation mergeBrainEntity(
    $teamId: ID!
    $fromEntityId: ID!
    $intoEntityId: ID!
  ) {
    mergeBrainEntity(
      teamId: $teamId
      fromEntityId: $fromEntityId
      intoEntityId: $intoEntityId
    ) {
      ...BrainEntityViewFields
    }
  }
  ${BRAIN_ENTITY_VIEW_FIELDS}
`;

const ESTIMATE_BRAIN_BACKFILL = gql`
  mutation estimateBrainBackfill(
    $teamId: ID!
    $sourceType: BrainSourceType!
    $channel: String
    $days: Int!
  ) {
    estimateBrainBackfill(
      teamId: $teamId
      sourceType: $sourceType
      channel: $channel
      days: $days
    ) {
      ...BrainBackfillPlanFields
    }
  }
  ${BRAIN_BACKFILL_PLAN_FIELDS}
`;

const START_BRAIN_BACKFILL = gql`
  mutation startBrainBackfill($teamId: ID!, $planId: ID!, $cap: Int!) {
    startBrainBackfill(teamId: $teamId, planId: $planId, cap: $cap) {
      ...BrainBackfillPlanFields
    }
  }
  ${BRAIN_BACKFILL_PLAN_FIELDS}
`;

const PAUSE_BRAIN_BACKFILL = gql`
  mutation pauseBrainBackfill($teamId: ID!, $planId: ID!) {
    pauseBrainBackfill(teamId: $teamId, planId: $planId) {
      ...BrainBackfillPlanFields
    }
  }
  ${BRAIN_BACKFILL_PLAN_FIELDS}
`;

const BRAIN_DISCOVERY_RUN_FIELDS = gql`
  fragment BrainDiscoveryRunFields on BrainDiscoveryRunView {
    runId
    status
    windowDays
    counts {
      messages
      members
      threads
      areas
      entities
      lowConfidence
      truncated
      truncatedBy
      peopleCount
      edgeCount
      meetings
      hierarchyMode
    }
    sources {
      slackChannelIds
      emailEnabled
      memberUserIds
      calendarEnabled
    }
    cost {
      haikuCalls
      inputTokens
      outputTokens
    }
    confidenceSummary {
      high
      medium
      low
    }
    startedAt
    finishedAt
    errorCode
    draftVersion
    historyLoadStarted
  }
`;

const GET_BRAIN_DISCOVERY_RUN = gql`
  query brainDiscoveryRun($teamId: ID!) {
    brainDiscoveryRun(teamId: $teamId) {
      ...BrainDiscoveryRunFields
    }
  }
  ${BRAIN_DISCOVERY_RUN_FIELDS}
`;

const START_BRAIN_DISCOVERY = gql`
  mutation startBrainDiscovery(
    $teamId: ID!
    $windowDays: Int
    $sources: BrainDiscoverySourcesInput!
  ) {
    startBrainDiscovery(
      teamId: $teamId
      windowDays: $windowDays
      sources: $sources
    ) {
      ...BrainDiscoveryRunFields
    }
  }
  ${BRAIN_DISCOVERY_RUN_FIELDS}
`;

const CONFIRM_BRAIN_EDGE = gql`
  mutation confirmBrainEdge(
    $teamId: ID!
    $fromEntityId: ID!
    $relation: BrainEdgeRelation!
    $toEntityId: ID!
  ) {
    confirmBrainEdge(
      teamId: $teamId
      fromEntityId: $fromEntityId
      relation: $relation
      toEntityId: $toEntityId
    ) {
      relation
      status
      suggested
    }
  }
`;

const DISMISS_BRAIN_EDGE = gql`
  mutation dismissBrainEdge(
    $teamId: ID!
    $fromEntityId: ID!
    $relation: BrainEdgeRelation!
    $toEntityId: ID!
  ) {
    dismissBrainEdge(
      teamId: $teamId
      fromEntityId: $fromEntityId
      relation: $relation
      toEntityId: $toEntityId
    )
  }
`;

export const GET_BRAIN_ASSERTION = gql`
  query brainAssertion($teamId: ID!, $assertionId: ID!) {
    brainAssertion(teamId: $teamId, assertionId: $assertionId) {
      ...BrainAssertionViewFields
    }
  }
  ${BRAIN_ASSERTION_VIEW_FIELDS}
`;

const SEARCH_BRAIN_ASSERTIONS = gql`
  query brainSearchAssertions($teamId: ID!, $query: String!, $limit: Int) {
    brainSearchAssertions(teamId: $teamId, query: $query, limit: $limit) {
      ...BrainAssertionViewFields
    }
  }
  ${BRAIN_ASSERTION_VIEW_FIELDS}
`;

const SAVE_BRAIN_BLUEPRINT_DRAFT = gql`
  mutation saveBrainBlueprintDraft(
    $teamId: ID!
    $input: BrainBlueprintInput!
    $expectedVersion: Int
  ) {
    saveBrainBlueprintDraft(
      teamId: $teamId
      input: $input
      expectedVersion: $expectedVersion
    ) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const RESPOND_TO_BRAIN_SUGGESTED_SIGNAL = gql`
  mutation respondToBrainSuggestedSignal(
    $teamId: ID!
    $templateId: String!
    $keep: Boolean!
    $texts: BrainSignalTemplateTextsInput
  ) {
    respondToBrainSuggestedSignal(
      teamId: $teamId
      templateId: $templateId
      keep: $keep
      texts: $texts
    ) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const CONFIRM_BRAIN_SUGGESTED_PERSON = gql`
  mutation confirmBrainSuggestedPerson(
    $teamId: ID!
    $personRef: ID!
    $keep: Boolean!
  ) {
    confirmBrainSuggestedPerson(
      teamId: $teamId
      personRef: $personRef
      keep: $keep
    ) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const GET_BRAIN_SUGGESTED_PERSON_EMAIL = gql`
  query brainSuggestedPersonEmail($teamId: ID!, $personRef: String!) {
    brainSuggestedPersonEmail(teamId: $teamId, personRef: $personRef)
  }
`;

const GET_BRAIN_PERSON_INVITE_EMAIL = gql`
  query brainPersonInviteEmail($teamId: ID!, $entityId: ID!) {
    brainPersonInviteEmail(teamId: $teamId, entityId: $entityId)
  }
`;

const ACTIVATE_BRAIN_BLUEPRINT = gql`
  mutation activateBrainBlueprint($teamId: ID!, $version: Int!) {
    activateBrainBlueprint(teamId: $teamId, version: $version) {
      ...BrainBlueprintFields
    }
  }
  ${BRAIN_BLUEPRINT_FIELDS}
`;

const RESPOND_TO_BRAIN_REVIEW = gql`
  mutation respondToBrainReview(
    $teamId: ID!
    $assertionId: ID!
    $decision: BrainReviewDecision!
    $note: String
  ) {
    respondToBrainReview(
      teamId: $teamId
      assertionId: $assertionId
      decision: $decision
      note: $note
    ) {
      ...BrainAssertionViewFields
    }
  }
  ${BRAIN_ASSERTION_VIEW_FIELDS}
`;

interface RespondToBrainSuggestedSignalData {
  respondToBrainSuggestedSignal: BrainBlueprintView;
}

interface ConfirmBrainSuggestedPersonData {
  confirmBrainSuggestedPerson: BrainBlueprintView;
}

interface BrainSuggestedPersonEmailData {
  brainSuggestedPersonEmail: string | null;
}

interface BrainPersonInviteEmailData {
  brainPersonInviteEmail: string | null;
}

interface BrainBlueprintData {
  brainBlueprint: BrainBlueprintView | null;
}

interface BrainBlueprintVersionsData {
  brainBlueprintVersions: BrainBlueprintView[];
}

interface BrainSlackChannelOptionsData {
  brainSlackChannelOptions: BrainSlackChannelOption[];
}

interface BrainStatsData {
  brainStats: BrainStats;
}

export interface BrainAssertionData {
  brainAssertion: BrainAssertionView | null;
}

interface BrainSearchAssertionsData {
  brainSearchAssertions: BrainAssertionView[];
}

interface SaveBrainBlueprintDraftData {
  saveBrainBlueprintDraft: BrainBlueprintView;
}

interface ActivateBrainBlueprintData {
  activateBrainBlueprint: BrainBlueprintView;
}

interface RespondToBrainReviewData {
  respondToBrainReview: BrainAssertionView;
}

function unwrapResult<TData>(result: {
  data?: TData;
  error?: unknown;
}): TData | undefined {
  if (result.error) {
    throw result.error;
  }
  return result.data;
}

export function useGetBrainBlueprint() {
  const client = useApolloClient();

  const getBrainBlueprint = useCallback(
    async (teamId: string): Promise<BrainBlueprintView | null> => {
      const data = unwrapResult(
        await client.query<BrainBlueprintData>({
          query: GET_BRAIN_BLUEPRINT,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainBlueprint ?? null;
    },
    [client],
  );

  return { getBrainBlueprint };
}

export function useGetBrainBlueprintVersions() {
  const client = useApolloClient();

  const getBrainBlueprintVersions = useCallback(
    async (teamId: string): Promise<BrainBlueprintView[]> => {
      const data = unwrapResult(
        await client.query<BrainBlueprintVersionsData>({
          query: GET_BRAIN_BLUEPRINT_VERSIONS,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainBlueprintVersions ?? [];
    },
    [client],
  );

  return { getBrainBlueprintVersions };
}

export function useGetBrainSlackChannelOptions() {
  const client = useApolloClient();

  const getBrainSlackChannelOptions = useCallback(
    async (teamId: string): Promise<BrainSlackChannelOption[]> => {
      const data = unwrapResult(
        await client.query<BrainSlackChannelOptionsData>({
          query: GET_BRAIN_SLACK_CHANNEL_OPTIONS,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainSlackChannelOptions ?? [];
    },
    [client],
  );

  return { getBrainSlackChannelOptions };
}

export function useGetBrainStats() {
  const client = useApolloClient();

  const getBrainStats = useCallback(
    async (teamId: string): Promise<BrainStats | null> => {
      const data = unwrapResult(
        await client.query<BrainStatsData>({
          query: GET_BRAIN_STATS,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainStats ?? null;
    },
    [client],
  );

  return { getBrainStats };
}

interface BrainAssertionQueryParams {
  teamId: string;
  assertionId: string;
  skip?: boolean;
}

export function useBrainAssertionQuery({
  teamId,
  assertionId,
  skip = false,
}: BrainAssertionQueryParams) {
  return useQuery<BrainAssertionData>(GET_BRAIN_ASSERTION, {
    variables: { teamId, assertionId },
    skip: skip || !teamId || !assertionId,
  });
}

export function useSearchBrainAssertions() {
  const client = useApolloClient();

  const searchBrainAssertions = useCallback(
    async (
      teamId: string,
      query: string,
      limit: number,
    ): Promise<BrainAssertionView[]> => {
      const data = unwrapResult(
        await client.query<BrainSearchAssertionsData>({
          query: SEARCH_BRAIN_ASSERTIONS,
          variables: { teamId, query, limit },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainSearchAssertions ?? [];
    },
    [client],
  );

  return { searchBrainAssertions };
}

export function useSaveBrainBlueprintDraft() {
  const [mutate, { loading }] = useMutation<SaveBrainBlueprintDraftData>(
    SAVE_BRAIN_BLUEPRINT_DRAFT,
  );

  const saveBrainBlueprintDraft = useCallback(
    async (
      teamId: string,
      input: BrainBlueprintInput,
      expectedVersion: number | null,
    ): Promise<BrainBlueprintView> => {
      const { data, error } = await mutate({
        variables: { teamId, input, expectedVersion },
      });
      if (error || !data) {
        throw error ?? new Error('saveBrainBlueprintDraft returned no data');
      }
      return data.saveBrainBlueprintDraft;
    },
    [mutate],
  );

  return { saveBrainBlueprintDraft, loading };
}

export function useConfirmBrainSuggestedPerson() {
  const [mutate, { loading }] = useMutation<ConfirmBrainSuggestedPersonData>(
    CONFIRM_BRAIN_SUGGESTED_PERSON,
  );

  const confirmBrainSuggestedPerson = useCallback(
    async (
      teamId: string,
      personRef: string,
      keep: boolean,
    ): Promise<BrainBlueprintView> => {
      const result = await mutate({ variables: { teamId, personRef, keep } });
      return requireMutationData(result, 'confirmBrainSuggestedPerson')
        .confirmBrainSuggestedPerson;
    },
    [mutate],
  );

  return { confirmBrainSuggestedPerson, loading };
}

export function useRespondToBrainSuggestedSignal() {
  const [mutate, { loading }] = useMutation<RespondToBrainSuggestedSignalData>(
    RESPOND_TO_BRAIN_SUGGESTED_SIGNAL,
  );

  const respondToBrainSuggestedSignal = useCallback(
    async (
      teamId: string,
      templateId: string,
      keep: boolean,
      texts?: BrainSignalTemplateTextsInput,
    ): Promise<BrainBlueprintView> => {
      const result = await mutate({
        variables: { teamId, templateId, keep, texts },
      });
      return requireMutationData(result, 'respondToBrainSuggestedSignal')
        .respondToBrainSuggestedSignal;
    },
    [mutate],
  );

  return { respondToBrainSuggestedSignal, loading };
}

export function useGetBrainSuggestedPersonEmail() {
  const client = useApolloClient();

  const getBrainSuggestedPersonEmail = useCallback(
    async (teamId: string, personRef: string): Promise<string | null> => {
      const data = unwrapResult(
        await client.query<BrainSuggestedPersonEmailData>({
          query: GET_BRAIN_SUGGESTED_PERSON_EMAIL,
          variables: { teamId, personRef },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainSuggestedPersonEmail ?? null;
    },
    [client],
  );

  return { getBrainSuggestedPersonEmail };
}

export function useGetBrainPersonInviteEmail() {
  const client = useApolloClient();

  const getBrainPersonInviteEmail = useCallback(
    async (teamId: string, entityId: string): Promise<string | null> => {
      const data = unwrapResult(
        await client.query<BrainPersonInviteEmailData>({
          query: GET_BRAIN_PERSON_INVITE_EMAIL,
          variables: { teamId, entityId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainPersonInviteEmail ?? null;
    },
    [client],
  );

  return { getBrainPersonInviteEmail };
}

export function useActivateBrainBlueprint() {
  const [mutate, { loading }] = useMutation<ActivateBrainBlueprintData>(
    ACTIVATE_BRAIN_BLUEPRINT,
  );

  const activateBrainBlueprint = useCallback(
    async (teamId: string, version: number): Promise<BrainBlueprintView> => {
      const { data, error } = await mutate({ variables: { teamId, version } });
      if (error || !data) {
        throw error ?? new Error('activateBrainBlueprint returned no data');
      }
      return data.activateBrainBlueprint;
    },
    [mutate],
  );

  return { activateBrainBlueprint, loading };
}

export function useRespondToBrainReviewService() {
  const [mutate, { loading }] = useMutation<RespondToBrainReviewData>(
    RESPOND_TO_BRAIN_REVIEW,
  );

  const respondToBrainReview = useCallback(
    async (
      teamId: string,
      assertionId: string,
      decision: BrainReviewDecision,
    ): Promise<BrainAssertionView> => {
      const { data, error } = await mutate({
        variables: { teamId, assertionId, decision },
        update(cache, result) {
          const assertion = result.data?.respondToBrainReview;
          if (!assertion) {
            return;
          }
          cache.writeQuery<BrainAssertionData>({
            query: GET_BRAIN_ASSERTION,
            variables: { teamId, assertionId },
            data: { brainAssertion: assertion },
          });
        },
      });
      if (error || !data) {
        throw error ?? new Error('respondToBrainReview returned no data');
      }
      return data.respondToBrainReview;
    },
    [mutate],
  );

  return { respondToBrainReview, loading };
}

interface BrainEntityTreeData {
  brainEntityTree: BrainEntityView[];
}

interface BrainEntitiesNeedingConfirmationData {
  brainEntitiesNeedingConfirmation: BrainEntityView[];
}

interface BrainBackfillPlansData {
  brainBackfillPlans: BrainBackfillPlanView[];
}

interface ConfirmBrainEntityData {
  confirmBrainEntity: BrainEntityView;
}

interface MergeBrainEntityData {
  mergeBrainEntity: BrainEntityView;
}

interface EstimateBrainBackfillData {
  estimateBrainBackfill: BrainBackfillPlanView;
}

interface StartBrainBackfillData {
  startBrainBackfill: BrainBackfillPlanView;
}

interface PauseBrainBackfillData {
  pauseBrainBackfill: BrainBackfillPlanView;
}

interface BrainDiscoveryRunData {
  brainDiscoveryRun: BrainDiscoveryRunView | null;
}

interface StartBrainDiscoveryData {
  startBrainDiscovery: BrainDiscoveryRunView;
}

interface ConfirmBrainEdgeData {
  confirmBrainEdge: Pick<BrainEdgeView, 'relation' | 'status' | 'suggested'>;
}

interface DismissBrainEdgeData {
  dismissBrainEdge: boolean;
}

function requireMutationData<TData>(
  result: { data?: TData | null; error?: unknown },
  operation: string,
): TData {
  if (result.error || !result.data) {
    throw result.error ?? new Error(`${operation} returned no data`);
  }
  return result.data;
}

export function useGetBrainEntityTree() {
  const client = useApolloClient();

  const getBrainEntityTree = useCallback(
    async (
      teamId: string,
      areaKey: string | null,
    ): Promise<BrainEntityView[]> => {
      const data = unwrapResult(
        await client.query<BrainEntityTreeData>({
          query: GET_BRAIN_ENTITY_TREE,
          variables: { teamId, areaKey },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainEntityTree ?? [];
    },
    [client],
  );

  return { getBrainEntityTree };
}

export function useGetBrainEntitiesNeedingConfirmation() {
  const client = useApolloClient();

  const getBrainEntitiesNeedingConfirmation = useCallback(
    async (teamId: string, limit: number): Promise<BrainEntityView[]> => {
      const data = unwrapResult(
        await client.query<BrainEntitiesNeedingConfirmationData>({
          query: GET_BRAIN_ENTITIES_NEEDING_CONFIRMATION,
          variables: { teamId, limit },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainEntitiesNeedingConfirmation ?? [];
    },
    [client],
  );

  return { getBrainEntitiesNeedingConfirmation };
}

interface BrainGraphData {
  brainGraph: BrainEdgeView[];
}

interface BrainPeopleData {
  brainPeople: BrainEntityView[];
}

export function useGetBrainGraph() {
  const client = useApolloClient();

  const getBrainGraph = useCallback(
    async (
      teamId: string,
      entityId: string,
      relation: BrainEdgeRelation | null = null,
    ): Promise<BrainEdgeView[]> => {
      const data = unwrapResult(
        await client.query<BrainGraphData>({
          query: GET_BRAIN_GRAPH,
          variables: { teamId, entityId, relation },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainGraph ?? [];
    },
    [client],
  );

  return { getBrainGraph };
}

export function useGetBrainPeople() {
  const client = useApolloClient();

  const getBrainPeople = useCallback(
    async (teamId: string): Promise<BrainEntityView[]> => {
      const data = unwrapResult(
        await client.query<BrainPeopleData>({
          query: GET_BRAIN_PEOPLE,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainPeople ?? [];
    },
    [client],
  );

  return { getBrainPeople };
}

export function useGetBrainBackfillPlans() {
  const client = useApolloClient();

  const getBrainBackfillPlans = useCallback(
    async (teamId: string): Promise<BrainBackfillPlanView[]> => {
      const data = unwrapResult(
        await client.query<BrainBackfillPlansData>({
          query: GET_BRAIN_BACKFILL_PLANS,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainBackfillPlans ?? [];
    },
    [client],
  );

  return { getBrainBackfillPlans };
}

export function useConfirmBrainEntity() {
  const [mutate, { loading }] =
    useMutation<ConfirmBrainEntityData>(CONFIRM_BRAIN_ENTITY);

  const confirmBrainEntity = useCallback(
    async (
      teamId: string,
      entityId: string,
      input: ConfirmBrainEntityInput,
    ): Promise<BrainEntityView> => {
      const result = await mutate({
        variables: { teamId, entityId, ...input },
      });
      return requireMutationData(result, 'confirmBrainEntity')
        .confirmBrainEntity;
    },
    [mutate],
  );

  return { confirmBrainEntity, loading };
}

export function useMergeBrainEntity() {
  const [mutate, { loading }] =
    useMutation<MergeBrainEntityData>(MERGE_BRAIN_ENTITY);

  const mergeBrainEntity = useCallback(
    async (
      teamId: string,
      fromEntityId: string,
      intoEntityId: string,
    ): Promise<BrainEntityView> => {
      const result = await mutate({
        variables: { teamId, fromEntityId, intoEntityId },
      });
      return requireMutationData(result, 'mergeBrainEntity').mergeBrainEntity;
    },
    [mutate],
  );

  return { mergeBrainEntity, loading };
}

export function useEstimateBrainBackfill() {
  const [mutate, { loading }] = useMutation<EstimateBrainBackfillData>(
    ESTIMATE_BRAIN_BACKFILL,
  );

  const estimateBrainBackfill = useCallback(
    async (
      teamId: string,
      input: EstimateBrainBackfillInput,
    ): Promise<BrainBackfillPlanView> => {
      const result = await mutate({ variables: { teamId, ...input } });
      return requireMutationData(result, 'estimateBrainBackfill')
        .estimateBrainBackfill;
    },
    [mutate],
  );

  return { estimateBrainBackfill, loading };
}

export function useStartBrainBackfill() {
  const [mutate, { loading }] =
    useMutation<StartBrainBackfillData>(START_BRAIN_BACKFILL);

  const startBrainBackfill = useCallback(
    async (
      teamId: string,
      planId: string,
      cap: number,
    ): Promise<BrainBackfillPlanView> => {
      const result = await mutate({ variables: { teamId, planId, cap } });
      return requireMutationData(result, 'startBrainBackfill')
        .startBrainBackfill;
    },
    [mutate],
  );

  return { startBrainBackfill, loading };
}

export function usePauseBrainBackfill() {
  const [mutate, { loading }] =
    useMutation<PauseBrainBackfillData>(PAUSE_BRAIN_BACKFILL);

  const pauseBrainBackfill = useCallback(
    async (teamId: string, planId: string): Promise<BrainBackfillPlanView> => {
      const result = await mutate({ variables: { teamId, planId } });
      return requireMutationData(result, 'pauseBrainBackfill')
        .pauseBrainBackfill;
    },
    [mutate],
  );

  return { pauseBrainBackfill, loading };
}

export function useGetBrainDiscoveryRun() {
  const client = useApolloClient();

  const getBrainDiscoveryRun = useCallback(
    async (teamId: string): Promise<BrainDiscoveryRunView | null> => {
      const data = unwrapResult(
        await client.query<BrainDiscoveryRunData>({
          query: GET_BRAIN_DISCOVERY_RUN,
          variables: { teamId },
          fetchPolicy: NETWORK_ONLY,
        }),
      );
      return data?.brainDiscoveryRun ?? null;
    },
    [client],
  );

  return { getBrainDiscoveryRun };
}

export function useStartBrainDiscovery() {
  const [mutate, { loading }] = useMutation<StartBrainDiscoveryData>(
    START_BRAIN_DISCOVERY,
  );

  const startBrainDiscovery = useCallback(
    async (
      teamId: string,
      windowDays: number,
      sources: BrainDiscoverySources,
    ): Promise<BrainDiscoveryRunView> => {
      const result = await mutate({
        variables: { teamId, windowDays, sources },
      });
      return requireMutationData(result, 'startBrainDiscovery')
        .startBrainDiscovery;
    },
    [mutate],
  );

  return { startBrainDiscovery, loading };
}

export function useConfirmBrainEdge() {
  const [mutate, { loading }] =
    useMutation<ConfirmBrainEdgeData>(CONFIRM_BRAIN_EDGE);

  const confirmBrainEdge = useCallback(
    async (teamId: string, edge: BrainEdgeRef) => {
      const result = await mutate({ variables: { teamId, ...edge } });
      return requireMutationData(result, 'confirmBrainEdge').confirmBrainEdge;
    },
    [mutate],
  );

  return { confirmBrainEdge, loading };
}

export function useDismissBrainEdge() {
  const [mutate, { loading }] =
    useMutation<DismissBrainEdgeData>(DISMISS_BRAIN_EDGE);

  const dismissBrainEdge = useCallback(
    async (teamId: string, edge: BrainEdgeRef): Promise<boolean> => {
      const result = await mutate({ variables: { teamId, ...edge } });
      return requireMutationData(result, 'dismissBrainEdge').dismissBrainEdge;
    },
    [mutate],
  );

  return { dismissBrainEdge, loading };
}
